import { useEffect, useRef, useState } from "react";
import { channelKey, MessagesChannel } from "../models/MessagesChannel";
import { LiveHandlers, LiveStatus } from "./messagesHubClient";
import { getMessagesHubClient } from "./messagesHubClients";

const isVisible = (): boolean => typeof document === "undefined" || document.visibilityState === "visible";

/**
 * Keeps the app's live messages connection open while the calling screen is mounted (US-56) and
 * routes its signals to `handlers`; watches `watchThreadId` when given. A hidden tab ignores
 * signals and resyncs when it comes back, like the poll it replaces. Returns the connection
 * status: callers poll only while it is not `connected`. A null channel opens nothing. A new target
 * on the same connection (the guardian picks a ward) resyncs once, as no signal would announce it.
 */
export const useMessagesLive = (
  channel: MessagesChannel | null,
  handlers: LiveHandlers,
  watchThreadId?: string,
): LiveStatus => {
  const kind = channel?.kind ?? null;
  const target = channel ? channelKey(channel) : null;
  const latest = useRef(handlers);
  latest.current = handlers;
  const [status, setStatus] = useState<LiveStatus>(() => (kind ? getMessagesHubClient(kind).getStatus() : "connecting"));

  useEffect(() => {
    if (!kind) return undefined;
    const client = getMessagesHubClient(kind);
    const release = client.retain();
    const offStatus = client.onStatus(setStatus);
    setStatus(client.getStatus());
    const unsubscribe = client.subscribe({
      onThreadChanged: (threadId) => {
        if (isVisible()) latest.current.onThreadChanged?.(threadId);
      },
      onUnreadChanged: () => {
        if (isVisible()) latest.current.onUnreadChanged?.();
      },
      onPresenceChanged: () => {
        if (isVisible()) latest.current.onPresenceChanged?.();
      },
      onResync: () => {
        if (isVisible()) latest.current.onResync?.();
      },
    });
    const onVisible = () => {
      if (isVisible() && client.getStatus() === "connected") latest.current.onResync?.();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      unsubscribe();
      offStatus();
      release();
    };
  }, [kind]);

  // On mount, or when the target member changes while connected: no connect event will come, so load now.
  useEffect(() => {
    if (!kind) return;
    if (getMessagesHubClient(kind).getStatus() === "connected" && isVisible()) latest.current.onResync?.();
  }, [kind, target]);

  useEffect(() => {
    if (!kind || !watchThreadId) return undefined;
    return getMessagesHubClient(kind).watch(watchThreadId);
  }, [kind, watchThreadId]);

  return kind ? status : "connecting";
};
